import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { Agent, ActivityEvent } from '@/types/entities';
import { AGENT_DEPARTMENTS } from '@/config/agentDepartments.config';
import { AGENT_PERMISSION_LEVELS, AgentOperationalStatus } from '@/types/orchestrator';
import { Icon } from '@/components/ui/Icon';

/**
 * AI OPERATIONS — the visual operating map (Phase D §5–7). Center card is
 * the Ugarits Orchestrator; around it, one node per department, joined
 * against the REAL Agent[] records from dataService (same repository the
 * Agents module CRUDs against — this is not a parallel fake list). A
 * department with no matching agent record shows "Not yet configured"
 * rather than being invented. No task-execution engine exists yet, so
 * Current Task/Queue are always honestly empty — see §35, never faked.
 *
 * Desktop renders a graph-like grid (orchestrator on top, departments in
 * a responsive grid below with a connecting rail) rather than a
 * freehand SVG node graph — deliberately, so the exact same markup
 * degrades to a clean card list on mobile instead of an unreadable
 * shrunk graph (§6, §44).
 */
function mapOperationalStatus(agent: Agent | undefined): AgentOperationalStatus {
  if (!agent) return 'waiting_connector';
  if (agent.status === 'paused') return 'paused';
  if (agent.status === 'error') return 'error';
  if (agent.status === 'running') return 'active'; // "Working" is reserved for an actual running task record, which doesn't exist yet
  if (agent.status === 'idle') return 'idle';
  if (agent.status === 'configured_preview') return 'configured_preview';
  return 'waiting_connector'; // waiting_credentials and any unknown status
}

function opStatusTone(status: AgentOperationalStatus): 'good' | 'warn' | 'crit' | 'info' | 'neutral' {
  switch (status) {
    case 'active': case 'working': return 'good';
    case 'error': return 'crit';
    case 'paused': case 'waiting_approval': return 'warn';
    case 'idle': case 'configured_preview': return 'info';
    default: return 'neutral';
  }
}

export function AIOperationsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dataService.listAgents(), dataService.listActivity()])
      .then(([a, act]) => { setAgents(a); setActivity(act); })
      .finally(() => setLoading(false));
  }, []);

  const previewActivity = [...activity]
    .filter((a) => a.actorType === 'user' || a.actorType === 'system')
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .slice(0, 6);

  if (loading) return <div className="card" style={{ padding: 20 }}>{t('common.preview')}…</div>;

  return (
    <div>
      <h1>{t('nav.aiOperations')}</h1>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>{t('aiOperations.subtitle')}</p>

      <div className="orchestrator-card">
        <div className="orchestrator-card-icon"><Icon name="cpu" size={22} /></div>
        <div>
          <div className="orchestrator-card-title">{t('aiOperations.orchestrator')}</div>
          <div className="orchestrator-card-desc">{t('aiOperations.orchestratorDesc')}</div>
        </div>
      </div>

      <div className="overview-section-label" style={{ marginTop: 20 }}>{t('aiOperations.specialistAgents')}</div>
      <div className="agent-node-grid">
        {AGENT_DEPARTMENTS.map((dept) => {
          const agent = agents.find((a) => a.department === dept.department && !a.archived);
          const opStatus = mapOperationalStatus(agent);
          const permMeta = AGENT_PERMISSION_LEVELS.find((p) => p.level === dept.defaultPermission) ?? AGENT_PERMISSION_LEVELS[0];
          return (
            <div key={dept.department} className="agent-node">
              <div className="agent-node-head">
                <div className="agent-node-icon"><Icon name={dept.icon} size={16} /></div>
                <div>
                  <div className="agent-node-name">{t(dept.nameKey)}</div>
                  <div className="agent-node-tagline">{t(dept.taglineKey)}</div>
                </div>
                <span className={`badge ${opStatusTone(opStatus)}`}>{t(`aiOperations.opStatus.${opStatus}`)}</span>
              </div>

              {agent ? (
                <div className="agent-node-meta">
                  <MetaRow label={t('aiOperations.provider')} value={agent.provider} />
                  <MetaRow label={t('aiOperations.model')} value={agent.model} />
                  <MetaRow label={t('aiOperations.permission')} value={t(permMeta.labelKey)} />
                  <MetaRow label={t('aiOperations.currentTask')} value={t('aiOperations.none')} />
                  <MetaRow label={t('aiOperations.queue')} value="0" />
                  <MetaRow label={t('aiOperations.lastActivity')} value={agent.lastTaskAt ?? t('aiOperations.empty')} />
                  <MetaRow label={t('aiOperations.memoryScope')} value={dept.memoryDomains.length.toString() + ' domains'} />
                  <MetaRow label={t('aiOperations.lastError')} value={agent.lastErrorAt ?? t('aiOperations.noError')} />
                  <button className="agent-node-link" onClick={() => navigate('/agents')}>{t('aiOperations.viewAgent')}</button>
                </div>
              ) : (
                <div className="agent-node-meta">
                  <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{t('aiOperations.notConfigured')}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="overview-section-label" style={{ marginTop: 20 }}>{t('aiOperations.recentPreviewActivity')}</div>
      <div className="card" style={{ padding: 12 }}>
        {previewActivity.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('overview.noRecentActivity')}</div>
        ) : (
          previewActivity.map((a) => (
            <div key={a.id} className="overview-row" style={{ cursor: 'default' }}>
              <span>{a.actorLabel} — {a.action}</span>
              <span className={`badge ${a.result}`}>{a.result}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="agent-node-meta-row">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
